import { useMemo, useState } from "react";
import type { ChangeEvent } from "react";

import { useHistory } from "react-router-dom";
import { useAddCable, useGetEquipment, useGetOperation, useGetSpareCables } from "../api/api";
import { OperationLinePartial } from "./OperationLinePartial";
import type { Cable, OperationData, StaticEquipment, Id } from "../types/models";
import type { ShipAndOperationRouteProps } from "../types/routes";

const directionsFwd = ["Head", "Breast", "Spring"];
const directionsAft = ["Stern", "Breast", "Spring"];

type OperationLineNewProps = ShipAndOperationRouteProps & {
  location: {
    search: string;
  };
};

type AddCablePayload = {
  arrangement: string | undefined;
  cable_id: Id;
  mooring_bit_id?: Id;
};

export const OperationLineNew = ({
  location,
  match
}: OperationLineNewProps) => {
  const params = new URLSearchParams(location.search);
  const history = useHistory();
  const station = params.get("station");
  const { shipId, operationId } = match.params;

  const directions = station === "FWD" ? directionsFwd : directionsAft;

  const { data: equipment } = useGetEquipment(shipId) as {
    data?: StaticEquipment[];
  };
  const { data: operation } = useGetOperation(shipId, operationId) as {
    data?: OperationData;
  };
  // Spare-assigned OR unassigned cables — the lines a mooring bit can pass.
  const { data: spareCables = [] } = useGetSpareCables(shipId);
  const addCable = useAddCable(shipId, operationId, {
    onSuccess: () => {
      history.goBack();
    },
  }) as unknown as { mutate: (payload: AddCablePayload) => void };

  const winches = useMemo(() => {
    if (!equipment || !operation) return [];

    const stationEquipment = equipment.filter((x) => x.direction === station);
    const winches = stationEquipment.filter((x) => x.type === "winch");
    const mooringBits = stationEquipment.filter((x) => x.type === "mooring_bit");

    const usedCableIds = (operation.equipment || [])
      .map((e) => e?.cable?.id)
      .filter(Boolean);

    const winchesWithFilteredCables = winches.map((w) => {
      const filteredCables = (w.cables || []).filter((c) => {
        return !usedCableIds.includes(c.id);
      });

      return {
        ...w,
        cables: filteredCables,
      };
    });

    const availableSpareCables = spareCables.filter(
      (cable) => !usedCableIds.includes(cable.id)
    );

    const mooringBitsWithSpareCables = mooringBits.map((bit) => {
      return {
        ...bit,
        cables: availableSpareCables,
      };
    });

    return [...winchesWithFilteredCables, ...mooringBitsWithSpareCables];
  }, [equipment, operation, station, spareCables]);

  const [selectedCable, setSelectedCable] = useState<Cable | undefined>();
  const [selectedWinch, setSelectedWinch] = useState<StaticEquipment | undefined>();
  const [selectedDirection, setSelectedDirection] = useState<string | undefined>();

  const onWinchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const winch = winches.find((x) => x.id === event.target.value);
    setSelectedWinch(winch);
    setSelectedCable(undefined);
    setSelectedDirection(undefined);
  };

  const onCableChange = (event: ChangeEvent<HTMLInputElement>) => {
    const cable = selectedWinch?.cables?.find((x) => x.id === event.target.value);
    setSelectedCable(cable);
    setSelectedDirection(undefined);
  };

  const onDirectionChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedDirection(event.target.value);
  };

  return (
    <OperationLinePartial
      mode="create"
      shipId={shipId}
      operationId={operationId}
      selectedWinch={selectedWinch}
      onWinchChange={onWinchChange}
      winches={winches}
      selectedCable={selectedCable}
      onCableChange={onCableChange}
      selectedDirection={selectedDirection}
      onDirectionChange={onDirectionChange}
      directions={directions}
      onPrimaryButtonClick={() => {
        if (!selectedCable) {
          return;
        }
        const payload: AddCablePayload = {
          arrangement: selectedDirection,
          cable_id: selectedCable.id,
        };

        if (selectedWinch?.type === "mooring_bit") {
          payload.mooring_bit_id = selectedWinch.id;
        }

        addCable.mutate({
          ...payload,
        });
      }}
      onDeleteButtonClick={() => {}}
    />
  );
};
